import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, School, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import CEUCard from '../components/ui/CEUCard';
import Alert from '../components/ui/Alert';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import CycleRenewalBanner from '../components/banners/CycleRenewalBanner';
import CycleTimeline from '../components/progress/CycleTimeline';
import StateLicenseProgress from '../components/StateLicenseProgress';
import { useCycleRenewal } from '../hooks/useCycleRenewal';
import ceuService from '../services/unifiedCEUService';
import userService from '../services/unifiedUserService';
import { s3PhotoService } from '../services/s3PhotoService';
import { getRequirements } from '../config/ceuConfig';
import { calculateGeneralStudiesCap } from '../utils/ridCEUCap';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ceus, setCeus] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // CEU to delete
  
  // Automatic cycle renewal
  const { renewalInfo, dismissBanner } = useCycleRenewal(user, userProfile);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const getUserFirstName = () => {
    // Try user profile first (from DynamoDB)
    if (userProfile?.firstName) {
      return userProfile.firstName;
    }
    
    if (userProfile?.name) {
      return userProfile.name.split(' ')[0];
    }
    
    // Fall back to "there" if no name available
    return 'there';
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load user profile
      const userResult = await userService.getUser(user.userId);
      if (userResult.success) {
        setUserProfile(userResult.data);
        
        // Load fresh photo URL if user has a photo
        if (userResult.data.profilePhotoUrl) {
          const photoUrl = await s3PhotoService.getPhotoUrl(user.userId);
          if (photoUrl) {
            setProfilePhoto(photoUrl);
          }
        }
      }

      // Load CEUs
      const ceusResult = await ceuService.getCEUs(user.userId);
      if (ceusResult.success) {
        setCeus(ceusResult.data || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
      setCeus([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCEU = async (ceu) => {
    setConfirmDelete(ceu);
  };

  const confirmDeleteCEU = async () => {
    if (!confirmDelete) return;

    try {
      const ceuId = confirmDelete.ceuId || confirmDelete.id;
      const result = await ceuService.deleteCEU(user.userId, ceuId);
      
      if (result.success) {
        setCeus(ceus.filter(c => (c.ceuId || c.id) !== ceuId));
        setConfirmDelete(null);
      } else {
        console.error('Delete failed:', result);
        alert('Failed to delete CEU: ' + (result.error || 'Unknown error'));
        setConfirmDelete(null);
      }
    } catch (err) {
      console.error('Error deleting CEU:', err);
      alert('Failed to delete CEU: ' + err.message);
      setConfirmDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const currentCycleCEUs = ceus.filter(ceu => ceu.isCurrentCycle);
  
  // DEBUG: Log cycle information in development
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Dashboard Debug Info:', {
      totalCEUs: ceus.length,
      currentCycleCEUs: currentCycleCEUs.length,
      userCycleStart: userProfile?.currentCycleStart,
      userCycleEnd: userProfile?.currentCycleEnd,
      sampleCEU: ceus[0] ? {
        date: ceus[0].date,
        isCurrentCycle: ceus[0].isCurrentCycle,
        title: ceus[0].title?.substring(0, 30)
      } : 'No CEUs'
    });
  }
  
  // Get requirements based on user's certification
  const certifyingBody = userProfile?.certifyingBody || 'RID';
  const membershipType = userProfile?.membershipType || 'NIC';
  const requirements = getRequirements(certifyingBody, membershipType);
  
  const requiredCEUs = requirements?.totalRequired || 8.0;
  
  // Calculate category totals
  const professionalStudiesCEUs = currentCycleCEUs
    .filter(c => c.category === 'Professional Studies')
    .reduce((sum, c) => sum + (parseFloat(c.ceuHours) || 0), 0);
    
  const generalStudiesCEUs = currentCycleCEUs
    .filter(c => c.category === 'General Studies')
    .reduce((sum, c) => sum + (parseFloat(c.ceuHours) || 0), 0);
  
  // Apply RID 2.0 CEU cap for General Studies
  const generalStudiesCap = calculateGeneralStudiesCap(generalStudiesCEUs);
    
  const ppoCEUs = currentCycleCEUs
    .filter(c => {
      // Handle all PPO subcategory formats
      if (!c.subCategory) return false;
      if (c.subCategory === 'PPO') return true;
      if (typeof c.subCategory === 'string' && c.subCategory.includes('PPO')) return true;
      if (Array.isArray(c.subCategory) && c.subCategory.includes('PPO')) return true;
      return false;
    })
    .reduce((sum, c) => sum + (parseFloat(c.ceuHours) || 0), 0);
  
  // Calculate total - PPO is already included in Professional Studies, don't double count
  const totalCEUs = professionalStudiesCEUs + generalStudiesCap.capped;
  const progress = (totalCEUs / requiredCEUs) * 100;

  // Filter for unconfirmed CEUs (not received by RID/BEI) and limit to 6
  const recentUnconfirmedCEUs = currentCycleCEUs
    .filter(ceu => !ceu.ridReceived && !ceu.beiReceived)
    .slice(0, 6);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Cycle Renewal Banner */}
        <CycleRenewalBanner renewalInfo={renewalInfo} onDismiss={dismissBanner} />
        
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            {/* Profile Photo */}
            {profilePhoto ? (
              <img
                src={profilePhoto}
                alt={`${getUserFirstName()}'s profile photo`}
                className="w-20 h-20 rounded-full object-cover border-2 border-primary/20"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center border-2 border-primary/20">
                <User className="w-10 h-10 text-primary" />
              </div>
            )}
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Welcome back, {getUserFirstName()}
              </p>
            </div>
          </div>
          <Button variant="primary" onClick={() => navigate('/add-ceu')}>
            <Plus className="w-5 h-5 mr-2" />
            Add CEU
          </Button>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {/* Progress Card */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <School className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Current Cycle Progress</h2>
                <p className="text-sm text-gray-600">
                  {totalCEUs.toFixed(1)} of {requiredCEUs.toFixed(1)} CEUs completed
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-primary">{Math.round(progress)}%</div>
              <div className="text-sm text-gray-600">Complete</div>
            </div>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div 
              className="bg-gradient-hero h-full transition-all duration-500 rounded-full"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>

          {/* Cycle Timeline */}
          <CycleTimeline 
            cycleStartDate={userProfile?.currentCycleStart} 
            cycleEndDate={userProfile?.currentCycleEnd} 
          />
          
          {progress >= 100 && (
            <Alert variant="success" className="mt-4">
              Congratulations! You've completed your CEU requirements for this cycle.
            </Alert>
          )}
        </Card>

        {/* Requirements Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <h3 className="font-semibold text-gray-900 mb-2">Professional Studies</h3>
            <div className="text-2xl font-bold text-primary">
              {professionalStudiesCEUs.toFixed(1)} / {requirements?.requirements?.['Professional Studies']?.min?.toFixed(1) || '6.0'}
            </div>
            <p className="text-sm text-gray-600">CEUs completed</p>
            <p className="text-xs text-gray-500 mt-1">
              Min: {requirements?.requirements?.['Professional Studies']?.min?.toFixed(1) || '6.0'} CEUs
            </p>
          </Card>
          
          <Card>
            <h3 className="font-semibold text-gray-900 mb-2">General Studies</h3>
            <div className="text-2xl font-bold text-primary">
              {generalStudiesCap.capped.toFixed(1)} / {requirements?.requirements?.['General Studies']?.max?.toFixed(1) || '2.0'}
            </div>
            <p className="text-sm text-gray-600">CEUs completed</p>
            {generalStudiesCap.overflow > 0 && (
              <p className="text-xs text-amber-600 mt-1">
                +{generalStudiesCap.overflow.toFixed(1)} overflow (actual: {generalStudiesCap.actual.toFixed(1)})
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Max: {requirements?.requirements?.['General Studies']?.max?.toFixed(1) || '2.0'} CEUs
            </p>
          </Card>
          
          <Card>
            <h3 className="font-semibold text-gray-900 mb-2">PPO Activities</h3>
            <div className="text-2xl font-bold text-primary">
              {ppoCEUs.toFixed(1)} / {requirements?.requirements?.['PPO']?.min?.toFixed(1) || '1.0'}
            </div>
            <p className="text-sm text-gray-600">CEUs completed</p>
            <p className="text-xs text-gray-500 mt-1">
              Min: {requirements?.requirements?.['PPO']?.min?.toFixed(1) || '1.0'} CEUs
            </p>
          </Card>
        </div>

        {/* State License Progress */}
        <div>
          <h2 className="text-xl font-semibold mb-4">State License Requirements</h2>
          <StateLicenseProgress 
            userId={user.userId}
            userProfile={userProfile}
            currentCycleCEUs={currentCycleCEUs}
          />
        </div>

        {/* CEU List */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Recent CEUs (Pending Confirmation)</h2>
          {recentUnconfirmedCEUs.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <School className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No pending CEUs</h3>
                <p className="text-gray-600">All your CEUs have been confirmed by RID/BEI</p>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentUnconfirmedCEUs.map((ceu) => (
                <CEUCard
                  key={`${ceu.userId || userProfile?.userId}-${ceu.ceuId || ceu.id}`}
                  ceu={ceu}
                  userProfile={userProfile}
                  onEdit={(ceu) => navigate(`/edit-ceu/${ceu.ceuId || ceu.id}`)}
                  onDelete={handleDeleteCEU}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={confirmDeleteCEU}
        title="Delete CEU"
        message={`Are you sure you want to delete "${confirmDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}
